/* eslint-disable react-native/no-inline-styles */
import React, { useEffect, useState } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { useSelector } from 'react-redux';
import {
  differenceInCalendarDays,
  formatISO,
  startOfDay,
  endOfDay,
} from 'date-fns';
import PropTypes from 'prop-types';

import SociollaCard from '@components/sociolla/SociollaCard';
import SociollaText from '@components/sociolla/SociollaText';
import Button from '@components/form/Button';
import DatePicker from '@components/form/DatePicker';
import SalesTeamSearch from '@components/form/search/SalesTeamSearch';
import SalesPersonSearch from '@components/form/search/SalesPersonSearch';
import { MsB2b } from '@api/ms-b2b';
import Image from '@components/global/Image';
import Colors from '@global/colors';
import { INPUT_HEIGHT } from '@/constants';
import { isWeb } from '@global/screens';

// Restricts the Sales Team picker to the caller's own teams, as LPBScreen does.
const ownTeamsFilter = (user) =>
  user?.sales_team?.length
    ? { _id: { $in: user.sales_team.map((team) => team.id) } }
    : {};

// The backend rejects a longer range with a 400, so refuse it here rather than round-trip for
// it. Three months is its cap; six took 22.6 s and 240 MB RSS on a cold production run.
const MAX_RANGE_DAYS = 92;

const realId = (option) =>
  option?.id && option.id !== 'all' ? option.id : undefined;

const SalesmanFilterBar = ({ value, loading, onApply }) => {
  const user = useSelector((state) => state.auth.user);
  const role = user?.role?.code || '';
  const isSalesman = role === 'salesman';
  const ownTeams = user?.sales_team || [];
  const ownId = user?._id || user?.id;

  const [from, setFrom] = useState(value.from);
  const [to, setTo] = useState(value.to);
  const [salesTeam, setSalesTeam] = useState(null);
  const [salesmen, setSalesmen] = useState([]);
  const [error, setError] = useState('');
  // Only mobile collapses, and starts collapsed so the data is the first thing on screen.
  const collapsible = !isWeb;
  const [collapsed, setCollapsed] = useState(collapsible);
  const showFields = !collapsible || !collapsed;

  // The backend resolves a salesman to themselves and ignores both filters for that role.
  const teamId = realId(salesTeam);

  const salesmanFilter = () => {
    if (isSalesman) {
      return ownId ? { _id: ownId } : {};
    }
    if (teamId) {
      return { 'sales_team.id': teamId };
    }
    return ownTeams.length
      ? { 'sales_team.id': { $in: ownTeams.map((team) => team.id) } }
      : {};
  };

  const onChangeTeam = (option) => {
    setSalesTeam(option);
    // An out-of-team salesman id resolves to nothing on the backend: an empty dashboard.
    setSalesmen([]);
  };

  // The Sales Team dropdown has no "All", so one team is always selected, and the default is the
  // team the dropdown lists first. It is read with the dropdown's own first query — same endpoint,
  // same `customFilter`, same skip and limit — rather than from the dropdown itself, because on a
  // phone the bar starts collapsed and `SalesTeamSearch` is never mounted. Reading it from the
  // component would leave the dashboard with nothing to scope by and no first load at all.
  //
  // The screen no longer fetches on mount; this is what starts it. A caller with no team to select
  // still applies, so they get an empty dashboard rather than a permanent spinner.
  useEffect(() => {
    let cancelled = false;
    const resolveDefaultTeam = async () => {
      let first = null;
      try {
        const response = await MsB2b.getSalesTeam({
          params: { filter: ownTeamsFilter(user), skip: 0, limit: 10 },
        });
        const team = response?.data?.data?.[0];
        if (team) {
          first = { ...team, id: team._id || team.id };
        }
      } catch (fetchError) {
        // Load unscoped rather than blocking the dashboard on a failed lookup.
      }
      if (cancelled) {
        return;
      }
      if (first) {
        setSalesTeam(first);
      }
      onApply({ from, to, salesTeamId: first?.id, salesmanIds: [] });
    };
    resolveDefaultTeam();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = () => {
    if (!from || !to) {
      setError('Both dates are required');
      return;
    }
    if (new Date(to) < new Date(from)) {
      setError('The end date is before the start date');
      return;
    }
    if (
      differenceInCalendarDays(new Date(to), new Date(from)) > MAX_RANGE_DAYS
    ) {
      setError(`The range cannot exceed ${MAX_RANGE_DAYS} days`);
      return;
    }

    setError('');
    onApply({
      from,
      to,
      salesTeamId: teamId,
      salesmanIds: (salesmen || []).map(realId).filter(Boolean),
    });
  };

  return (
    <SociollaCard style={styles.card}>
      {collapsible ? (
        <TouchableOpacity
          activeOpacity={0.8}
          style={[styles.header, collapsed ? styles.headerCollapsed : null]}
          onPress={() => setCollapsed((prev) => !prev)}>
          <SociollaText type="medium" style={styles.headerText}>
            Filter by
          </SociollaText>
          <Image
            svg
            width={14}
            height={8}
            style={[styles.chevron, collapsed ? null : styles.chevronOpen]}
            source={require('@assets/img/icons/arrow-down-grey.svg')}
          />
        </TouchableOpacity>
      ) : null}

      {showFields ? (
        <>
          <View style={styles.row}>
            <View style={[styles.field, styles.periodField]}>
              <SociollaText style={styles.label}>Period</SociollaText>
              <View style={styles.periodRow}>
                {/* moreLeft keeps the calendar icon inside the input. The default pulls it out
                with a negative right margin, which lands it on the next field. */}
                <DatePicker
                  hideLabel
                  moreLeft
                  name="from_date"
                  disabled={loading}
                  style={styles.datePicker}
                  value={from}
                  onChange={(val) =>
                    setFrom(val ? formatISO(startOfDay(val)) : '')
                  }
                />
                <SociollaText style={styles.dateSeparator}>-</SociollaText>
                <DatePicker
                  hideLabel
                  moreLeft
                  name="to_date"
                  disabled={loading}
                  style={styles.datePicker}
                  value={to}
                  onChange={(val) => setTo(val ? formatISO(endOfDay(val)) : '')}
                />
              </View>
            </View>

            <View style={styles.field}>
              <SociollaText style={styles.label}>Sales Team</SociollaText>
              {/* No "All": one team is always selected. The default is resolved above with the
                same query this dropdown makes, so the two always agree on which comes first. */}
              <SalesTeamSearch
                hideLabel
                style={styles.select}
                placeholder="Select Sales Team"
                editable={!loading}
                customFilter={ownTeamsFilter(user)}
                value={salesTeam || {}}
                onChange={onChangeTeam}
              />
            </View>

            <View style={styles.field}>
              <SociollaText style={styles.label}>Salesman</SociollaText>
              <SalesPersonSearch
                hideLabel
                isAll
                multiple
                style={styles.select}
                placeholder="All Salesman"
                editable={!loading}
                value={salesmen}
                customFilter={salesmanFilter()}
                onChange={(val) => setSalesmen(val || [])}
              />
            </View>

            <View style={styles.applyWrapper}>
              {/* disabledStyle repeats the size: Button drops buttonStyle entirely when
                  disabled, so without it the button collapses to its content. */}
              <Button
                type="primary"
                rounded
                disabled={loading}
                buttonStyle={styles.applyBtn}
                disabledStyle={styles.applyBtn}
                title={
                  <SociollaText
                    type="medium"
                    style={[
                      styles.applyBtnText,
                      loading ? styles.applyBtnTextDisabled : null,
                    ]}>
                    Apply
                  </SociollaText>
                }
                onPress={apply}
              />
            </View>
          </View>

          {error ? (
            <SociollaText style={styles.error}>{error}</SociollaText>
          ) : null}
        </>
      ) : null}
    </SociollaCard>
  );
};

SalesmanFilterBar.propTypes = {
  value: PropTypes.shape({
    from: PropTypes.string,
    to: PropTypes.string,
  }).isRequired,
  loading: PropTypes.bool,
  onApply: PropTypes.func.isRequired,
};

SalesmanFilterBar.defaultProps = { loading: false };

const LABEL_BLOCK = 22;

const styles = StyleSheet.create({
  applyBtn: { height: INPUT_HEIGHT, width: 110 },
  applyBtnText: { color: Colors.white, fontSize: 14 },
  // buttonTextDisabled is white, which is invisible on the #e5e5e5 disabled background.
  applyBtnTextDisabled: { color: Colors.grey13 },
  // Mobile: always its own line (the mockup); only a 100% basis guarantees that wrap.
  applyWrapper: isWeb
    ? // The button has no label, so it carries the label block's height as a margin to sit level
      // with the inputs beside it.
      { marginLeft: 'auto', marginTop: LABEL_BLOCK }
    : { alignItems: 'flex-end', flexBasis: '100%' },
  chevron: { height: 8, width: 14 },
  chevronOpen: { transform: [{ rotate: '180deg' }] },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 12,
  },
  headerCollapsed: { paddingBottom: 0 },
  headerText: { fontSize: 16 },
  // Overrides SociollaCard's marginBottom 30/20; the screen owns the gap below this card.
  card: { marginBottom: 0, padding: 16 },
  datePicker: { flexBasis: 135, flexGrow: 1, flexShrink: 1, minWidth: 135 },
  dateSeparator: { color: Colors.grey13, paddingHorizontal: 8 },
  error: { color: Colors.red3, fontSize: 12, marginTop: 8 },
  field: { flexBasis: 140, flexGrow: 1, minWidth: 140 },
  // Caps the ~150px intrinsic minimum of the <input> inside SelectBox on web.
  select: { width: '100%' },
  // paddingHorizontal matches formGroup in formStyles.js, so labels line up with controls.
  label: {
    color: Colors.grey13,
    fontSize: 12,
    height: LABEL_BLOCK,
    lineHeight: 16,
    paddingHorizontal: 6,
  },
  // No minWidth: a floor would push the field out of a 320px phone's card.
  periodField: { flexBasis: 320 },
  periodRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: 8,
  },
  // Top-aligned, not bottom: SelectBox in `multiple` mode (the Salesman field) always renders
  // its selected-values container, whose marginTop of 5 makes that field taller than the rest.
  // Wrapping is flexbox, never a branch on isWeb, which is frozen at module load.
  row: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
});

export default SalesmanFilterBar;
